import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { loginRouteWithReturn } from '@/lib/default-landing';
import { canAccessRoute, fallbackPathForRole } from '@/lib/permissions';

/** Profile path used for forced password change (DEF-247). */
const FORCE_PASSWORD_PATH = '/profile';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that checks authentication and role-based permissions.
 * Redirects to /login if not authenticated, or to a role-allowed landing
 * route when the user's role cannot access the requested path.
 * DEF-247 — mustChangePassword users are confined to /profile until they rotate.
 * Forced legal acceptance then holds the remaining gates (it is a blocking
 * modal in AppShell, not a redirect).
 * FR-23 — mustCompleteName (DV claim-completion flag) confines to /profile#name
 * after any forced password rotation.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, realUser } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // DEF-410 / FR-555 — keep the page, its tab, and its hash through sign-in.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate to={loginRouteWithReturn(returnTo)} state={{ from: location }} replace />
    );
  }

  if (
    user?.mustChangePassword &&
    location.pathname !== FORCE_PASSWORD_PATH
  ) {
    return (
      <Navigate
        to={`${FORCE_PASSWORD_PATH}#password`}
        replace
        state={{ forcePasswordChange: true }}
      />
    );
  }

  // Legal acceptance sits between the two: it is a blocking modal in AppShell,
  // not a redirect, so nothing happens here except holding back name capture
  // until they have accepted (otherwise they land on /profile#name behind the
  // modal). Impersonated sessions are exempt from the legal gate.
  const legalGateOpen = user?.mustAcceptLegal === true && !realUser;

  // After password rotation (or when none is required), FR-23 name capture.
  // Keyed on mustCompleteName — never on raw null names for other roles.
  if (
    !user?.mustChangePassword &&
    !legalGateOpen &&
    user?.mustCompleteName &&
    location.pathname !== FORCE_PASSWORD_PATH
  ) {
    return (
      <Navigate
        to={`${FORCE_PASSWORD_PATH}#name`}
        replace
        state={{ forceNameCapture: true }}
      />
    );
  }

  if (user && !canAccessRoute(user.role, location.pathname)) {
    const fallback = fallbackPathForRole(user.role);
    if (location.pathname !== fallback) {
      return <Navigate to={fallback} replace />;
    }
  }

  return <>{children}</>;
}
